import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  ViewChild,
  inject,
} from '@angular/core';
import { ProgressSpinner } from 'primeng/progressspinner';
import { NcpbService } from '@core/services/ncpb.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
  selector: 'ncp-eu-orcd',
  imports: [
    ProgressSpinner
  ],
  templateUrl: './orcd.component.html',
})
export class OrcdComponent {
  @ViewChild('documentViewer')
  private documentViewer?: ElementRef<HTMLDivElement>;

  private readonly ncpbService = inject(NcpbService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly sanitizer = inject(DomSanitizer);

  protected documentLoading = false;
  protected pdfUrl?: SafeResourceUrl;
  protected imageUrl?: SafeResourceUrl;

  protected loading = false;
  protected list: any[] = [];
  protected activeTab = '';
  protected errorMessage = '';

  private selectedCountry = JSON.parse(
    sessionStorage.getItem('selectedCountry') || '{}'
  );

  private patient = JSON.parse(
    sessionStorage.getItem('euPatient') || '{}'
  );

  protected setActiveTab(tab: string) {
    this.activeTab = tab;
    this.errorMessage = '';
    this.clearDocumentView();

    const classCodeMap: any = {
      '1': '34105-7',
      '2': '18748-4',
      '3': 'x-clinical-image',
      '4': '11502-2',
    };

    const classCode = classCodeMap[tab];

    if (!classCode) {
      return;
    }

    this.list = [];
    this.loading = true;
    this.cdr.detectChanges();

    this.ncpbService.orcd({
      countryCode: this.selectedCountry.countryCode,
      patientIdentifier: this.patient.identifier,
      purposeOfUse: 'TREATMENT',
      classCodes: [classCode],
    }).subscribe({
      next: (res: any) => {
        this.list = (res?.clinicalDocuments || [])
          .sort((a: any, b: any) =>
            new Date(b.creationDate || b.creationDt || '').getTime() -
            new Date(a.creationDate || a.creationDt || '').getTime()
          );

        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.list = [];
        this.errorMessage =
          err?.error?.message ||
          err?.error?.error ||
          err?.message ||
          'Unknown error';

        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  protected getDocumentTitle(item: any): string {
    return item?.description || item?.name || item?.title || item?.classCode || '';
  }

  protected getDocumentDate(item: any): string {
    return item?.creationDate || item?.creationDt || item?.eventDate || '';
  }

  protected retrieveDocument = (item: any) => {
    this.clearDocumentView();

    this.documentLoading = true;
    this.cdr.detectChanges();
    this.scrollToDocumentViewer();

    this.ncpbService.orcdRetrieve({
      countryCode: this.selectedCountry.countryCode,
      classCode: item.classCode,
      patientIdentifier: this.patient.identifier,
      purposeOfUse: 'TREATMENT',
      repositoryId: item.repositoryId,
      documentIdentifier: item.identifier,
      homeCommunityId: item.homeCommunityId,
    }).subscribe({
      next: (res: any) => {
        if (item.classCode === 'x-clinical-image') {
          const xml = atob(res.content || '');
          const doc = new DOMParser().parseFromString(xml, 'text/xml');
          const imageBase64 = doc.getElementsByTagName('text')[0]?.textContent?.trim();

          if (imageBase64) {
            this.imageUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
              `data:image/png;base64,${imageBase64}`
            );
          }
        } else {
          const pdfContent = res?.pdfContent || res?.content;

          if (pdfContent) {
            this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
              `data:application/pdf;base64,${pdfContent}`
            );
          }
        }

        this.documentLoading = false;
        this.cdr.detectChanges();
        this.scrollToDocumentViewer();
      },
      error: () => {
        this.documentLoading = false;
        this.cdr.detectChanges();
      },
    });
  };

  private clearDocumentView() {
    this.pdfUrl = undefined;
    this.imageUrl = undefined;
    this.documentLoading = false;
  }

  private scrollToDocumentViewer() {
    setTimeout(() => {
      this.documentViewer?.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }
}
